import type { Booking, BookingGarment, GarmentState } from '../api/bookings';
import { formatAmount } from '../format/money';
import { depositBlock, liveGarments } from '../print/customerMoney';
import { paymentKindLabel, paymentMethodLabel } from './paymentWords';
import { dayAfter } from './registerViews';

/**
 * The phone's booking screen as three tabs — Garments, Money, History — chosen by the owner on
 * 2026-10-19 from the canvas (https://claude.ai/artifact/F9fy4yjuWW3wq5BaUVpPKe, option B).
 *
 * <p>
 * <strong>Each tab says in its own label whether it needs looking at</strong> — "Garments · 3/4"
 * while some are still being got ready, "Money · ₹10k" while something is owed — so a clerk can
 * leave the screen on Garments and still see the money. The band carries the one date that
 * matters today ("Pick-up today", "2 days late"). History is built only from what the booking
 * already records; it never invents a date the server did not send.
 * </p>
 */

export type DetailTab = 'garments' | 'money' | 'history';

export type WhenTone = 'danger' | 'warning' | 'neutral';

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** Before the counter has handed the garment over — the states a "ready" count is about. */
const beforeHandOver: readonly GarmentState[] = [
  'Pending',
  'Allocated',
  'Fitting',
  'Alteration',
  'Ready',
];

const finished = (booking: Booking) =>
  booking.stage === 'Completed' || booking.stage === 'Cancelled' || booking.stage === 'Expired';

/**
 * The band's word on when — only when it is today, tomorrow or late. Any other day is in the dates
 * chip beside it, and a third chip saying "in 5 days" would be noise.
 */
export function whenChip(booking: Booking, today: string): { text: string; tone: WhenTone } | null {
  if (finished(booking)) {
    return null;
  }

  const live = liveGarments(booking);
  const handedOver = live.some((garment) => garment.issuedOn !== null);

  if (!handedOver) {
    const date = booking.possessionFromDate;

    if (date < today) {
      return { text: 'Not collected', tone: 'warning' };
    }

    if (date === today) {
      return { text: 'Pick-up today', tone: 'warning' };
    }

    return date === dayAfter(today) ? { text: 'Pick-up tomorrow', tone: 'neutral' } : null;
  }

  if (booking.isOverdue) {
    return {
      text: `${plural(Math.max(1, booking.daysOverdue), 'day', 'days')} late`,
      tone: 'danger',
    };
  }

  const date = booking.possessionToDate;

  if (date === today) {
    return { text: 'Back today', tone: 'warning' };
  }

  return date === dayAfter(today) ? { text: 'Back tomorrow', tone: 'neutral' } : null;
}

/**
 * "3/4" while some garments are still being got ready — handed over counts as ready — and the plain
 * count once none are, when a fraction would only ever read "4/4".
 */
export function garmentCount(booking: Booking): string {
  const live = liveGarments(booking);
  const waiting = live.filter((garment) => beforeHandOver.includes(garment.state));

  if (waiting.length === 0 || finished(booking)) {
    return String(live.length);
  }

  const ready = live.length - waiting.filter((garment) => garment.state !== 'Ready').length;

  return `${ready}/${live.length}`;
}

/**
 * A sum short enough for a tab: "₹850", "₹9.5k", "₹10k", "₹1.2L". Lakhs, not millions — the
 * shops count in them.
 */
export function compactAmount(amount: number, currency: string): string {
  const value = Math.abs(amount);
  const oneDecimal = (n: number) => String(Math.round(n * 10) / 10);

  if (value < 1000) {
    return `${currency}${Math.round(value)}`;
  }

  // 99,960 rounds to "100k", which is a lakh by another name.
  if (Math.round((value / 1000) * 10) / 10 < 100) {
    return `${currency}${oneDecimal(value / 1000)}k`;
  }

  return `${currency}${oneDecimal(value / 100000)}L`;
}

/**
 * What the customer still owes, rent and deposit together — the figure the Money tab carries in
 * its label. The deposit is not part of the invoice, but it is money the counter has to take.
 */
export function owedNow(booking: Booking): number {
  return Math.max(0, booking.balanceDue) + depositBlock(booking).due;
}

export function tabLabel(tab: DetailTab, booking: Booking, currency: string): string {
  switch (tab) {
    case 'garments':
      return `Garments · ${garmentCount(booking)}`;
    case 'money': {
      const owed = owedNow(booking);

      return owed > 0 ? `Money · ${compactAmount(owed, currency)}` : 'Money';
    }
    case 'history':
      return 'History';
  }
}

export interface HistoryEntry {
  /** The day, `YYYY-MM-DD` — the stored value's own date, as `formatDate` reads it. */
  day: string;
  text: string;
  /** After today: something planned rather than something that happened. */
  upcoming: boolean;
}

const dayOf = (value: string) => value.slice(0, 10);

function countByDay(
  garments: readonly BookingGarment[],
  on: (garment: BookingGarment) => string | null,
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const garment of garments) {
    const value = on(garment);

    if (value) {
      counts.set(dayOf(value), (counts.get(dayOf(value)) ?? 0) + 1);
    }
  }

  return counts;
}

/**
 * The booking's story in date order, from what it records: payments, fittings, hand-overs and
 * returns, the tailor's due date, and — while they are still to come — the pick-up, the event and
 * the day it is due back.
 *
 * <p>
 * There is no "quoted on" or "confirmed on": the booking does not carry those dates, and
 * `stageDates` stands the charge date in for the quotation's, which is fine for a tracker and wrong
 * for a dated line.
 * </p>
 */
export function bookingHistory(booking: Booking, today: string, currency: string): HistoryEntry[] {
  const entries: { day: string; text: string; order: number }[] = [];
  const add = (value: string | null | undefined, text: string) => {
    if (value) {
      entries.push({ day: dayOf(value), text, order: entries.length });
    }
  };
  const live = liveGarments(booking);
  const handedOver = live.some((garment) => garment.issuedOn !== null);

  for (const payment of booking.payments) {
    add(
      payment.receivedOn,
      `${paymentKindLabel[payment.kind]} ${currency}${formatAmount(Math.abs(payment.amount))} · ${paymentMethodLabel[payment.method]}`,
    );
  }

  for (const wearer of booking.wearers) {
    if (wearer.fittingCompletedOn) {
      add(wearer.fittingCompletedOn, `${wearer.name} fitted`);
    } else if (!finished(booking)) {
      add(wearer.fittingAppointmentOn, `${wearer.name}'s fitting`);
    }
  }

  for (const garment of live) {
    if (garment.state === 'Alteration') {
      add(
        garment.alterationDueOn,
        `${garment.itemName} due back from ${garment.alterationTailorName ?? 'the tailor'}`,
      );
    }
  }

  for (const [day, count] of countByDay(live, (garment) => garment.issuedOn)) {
    add(day, `Handed over ${plural(count, 'garment', 'garments')}`);
  }

  for (const [day, count] of countByDay(live, (garment) => garment.returnedOn)) {
    add(day, `${plural(count, 'garment', 'garments')} back`);
  }

  if (!finished(booking)) {
    if (booking.stage === 'Hold') {
      add(booking.holdExpiresOn, 'Hold ends');
    }

    if (!handedOver) {
      add(booking.possessionFromDate, 'Pick-up');
    }

    add(booking.eventDate, booking.eventKind ?? 'Event');
    add(booking.possessionToDate, 'Due back');
  }

  return entries
    .sort((a, b) => (a.day === b.day ? a.order - b.order : a.day < b.day ? -1 : 1))
    .map(({ day, text }) => ({ day, text, upcoming: day > today }));
}
